import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import '../config/api'; // Import to ensure axios interceptors are configured
import { useAuth } from './AuthContext';

/**
 * Available modules in the system
 */
export enum Module {
  CORE_OMS = 'CORE_OMS',
  IBOR = 'IBOR',
  COMPLIANCE = 'COMPLIANCE',
  RISK_ANALYTICS = 'RISK_ANALYTICS',
  AI_PORTFOLIO_CONSTRUCTION = 'AI_PORTFOLIO_CONSTRUCTION',
  AI_REBALANCER = 'AI_REBALANCER',
  AI_HEDGING = 'AI_HEDGING',
  AI_TAX_HARVESTING = 'AI_TAX_HARVESTING',
  MIDDLE_OFFICE = 'MIDDLE_OFFICE',
  // Middle Office sub-modules (gate individual tabs inside the hub)
  SWIFT_RECON = 'SWIFT_RECON',
  CORPORATE_ACTIONS = 'CORPORATE_ACTIONS',
  NAV_CALCULATION = 'NAV_CALCULATION',
  INSURANCE_REPORTING = 'INSURANCE_REPORTING',
  FIX_CONNECTIVITY = 'FIX_CONNECTIVITY',
  REGULATORY_REPORTING = 'REGULATORY_REPORTING',
  ALADDIN_INTEGRATION = 'ALADDIN_INTEGRATION',
  CRD_INTEGRATION = 'CRD_INTEGRATION',
  SIMCORP_INTEGRATION = 'SIMCORP_INTEGRATION',
  // Retail/RIA modules
  HOUSEHOLDS = 'HOUSEHOLDS',
  MODEL_PORTFOLIOS = 'MODEL_PORTFOLIOS',
  FEE_BILLING = 'FEE_BILLING',
  CLIENT_PORTAL = 'CLIENT_PORTAL',
  CUSTODIAN_SCHWAB = 'CUSTODIAN_SCHWAB',
  CUSTODIAN_FIDELITY = 'CUSTODIAN_FIDELITY',
  SLEEVE_UMA = 'SLEEVE_UMA',
  CLIENT_ONBOARDING = 'CLIENT_ONBOARDING',
  // Credit/Institutional modules (Beach Point Capital)
  LEVERAGED_LOANS = 'LEVERAGED_LOANS',
  CLO_MANAGEMENT = 'CLO_MANAGEMENT',
  ABS_TRADING = 'ABS_TRADING',
  CDS_TRADING = 'CDS_TRADING',
  DISTRESSED_DEBT = 'DISTRESSED_DEBT',
  COVENANT_MONITORING = 'COVENANT_MONITORING',
  COUNTERPARTY_ISDA = 'COUNTERPARTY_ISDA',
  REAL_ESTATE_DEBT = 'REAL_ESTATE_DEBT',
  SECONDARY_INTELLIGENCE = 'SECONDARY_INTELLIGENCE',
  CREDIT_ANALYTICS = 'CREDIT_ANALYTICS',
}

/**
 * License type
 */
export type LicenseType = 'STANDALONE' | 'ALADDIN_ADDON' | 'CRD_ADDON' | 'SIMCORP_ADDON' | 'GENERIC_ADDON';

/**
 * Module info with display metadata
 */
export interface ModuleInfo {
  module: string;
  displayName: string;
  description: string;
  includedByDefault: boolean;
  enabled: boolean;
}

/**
 * License tier info
 */
export interface TierInfo {
  tier: string;
  displayName: string;
  monthlyPriceDollars: number;
  annualPriceDollars: number;
  includedModules: string[];
}

/**
 * Deployment mode for UI adaptation
 */
export type DeploymentMode = 'standalone' | 'sidecar';

/**
 * Feature flag context state
 */
interface FeatureFlagContextState {
  // State
  enabledModules: Set<Module>;
  allModules: ModuleInfo[];
  licenseType: LicenseType;
  isSidecarMode: boolean;
  deploymentMode: DeploymentMode;
  isLoading: boolean;
  error: string | null;
  
  // Methods
  isModuleEnabled: (module: Module) => boolean;
  areModulesEnabled: (...modules: Module[]) => boolean;
  anyModuleEnabled: (...modules: Module[]) => boolean;
  refreshLicenses: () => Promise<void>;
  
  // AI module helpers
  hasAIPortfolioConstruction: boolean;
  hasAIRebalancer: boolean;
  hasAIHedging: boolean;
  hasAITaxHarvesting: boolean;
  hasAnyAIModule: boolean;
  
  // Other module helpers
  hasIBOR: boolean;
  hasCompliance: boolean;
  hasRiskAnalytics: boolean;
  hasMiddleOffice: boolean;
  
  // Sidecar-specific helpers
  hasSidecarFeatures: boolean;
  hasAladdinIntegration: boolean;
  hasCRDIntegration: boolean;
  hasSimCorpIntegration: boolean;
  externalPlatformName: string | null;

  // Credit module helpers (Beach Point Capital)
  hasLeveragedLoans: boolean;
  hasCLOManagement: boolean;
  hasABSTrading: boolean;
  hasCDSTrading: boolean;
  hasDistressedDebt: boolean;
  hasCovenantMonitoring: boolean;
  hasCounterpartyISDA: boolean;
  hasRealEstateDebt: boolean;
  hasSecondaryIntelligence: boolean;
  hasCreditAnalytics: boolean;
  hasAnyCreditModule: boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextState | undefined>(undefined);

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [enabledModules, setEnabledModules] = useState<Set<Module>>(new Set([Module.CORE_OMS]));
  const [allModules, setAllModules] = useState<ModuleInfo[]>([]);
  const [licenseType, setLicenseType] = useState<LicenseType>('STANDALONE');
  const [isSidecarMode, setIsSidecarMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLicenses = useCallback(async () => {
    // Only fetch licenses if user is authenticated
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await axios.get('/api/licenses/modules/enabled');
      const data = response.data;
      
      // Convert module strings to Set of Module enums
      const modules = new Set<Module>(
        data.modules.map((m: ModuleInfo) => m.module as Module)
      );
      
      setEnabledModules(modules);
      setLicenseType(data.licenseType || 'STANDALONE');
      setIsSidecarMode(data.sidecarMode || false);
      
      // Fetch all modules with their status
      const allModulesResponse = await axios.get('/api/licenses/modules/all');
      setAllModules(allModulesResponse.data);
      
    } catch (err: any) {
      // Silently fail if 403 (not authenticated) - this is expected before login
      if (err.response?.status !== 403) {
        console.error('Failed to fetch licenses:', err);
        setError(err.message || 'Failed to load license information');
      }
      // On error, default to CORE_OMS only
      setEnabledModules(new Set([Module.CORE_OMS]));
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  const isModuleEnabled = useCallback((module: Module): boolean => {
    return enabledModules.has(module);
  }, [enabledModules]);

  const areModulesEnabled = useCallback((...modules: Module[]): boolean => {
    return modules.every(m => enabledModules.has(m));
  }, [enabledModules]);

  const anyModuleEnabled = useCallback((...modules: Module[]): boolean => {
    return modules.some(m => enabledModules.has(m));
  }, [enabledModules]);

  // Computed properties for common module checks
  const hasAIPortfolioConstruction = enabledModules.has(Module.AI_PORTFOLIO_CONSTRUCTION);
  const hasAIRebalancer = enabledModules.has(Module.AI_REBALANCER);
  const hasAIHedging = enabledModules.has(Module.AI_HEDGING);
  const hasAITaxHarvesting = enabledModules.has(Module.AI_TAX_HARVESTING);
  const hasAnyAIModule = hasAIPortfolioConstruction || hasAIRebalancer || hasAIHedging || hasAITaxHarvesting;
  
  const hasIBOR = enabledModules.has(Module.IBOR);
  const hasCompliance = enabledModules.has(Module.COMPLIANCE);
  const hasRiskAnalytics = enabledModules.has(Module.RISK_ANALYTICS);
  const hasMiddleOffice = enabledModules.has(Module.MIDDLE_OFFICE);
  
  // Sidecar-specific computed properties
  const hasAladdinIntegration = enabledModules.has(Module.ALADDIN_INTEGRATION);
  const hasCRDIntegration = enabledModules.has(Module.CRD_INTEGRATION);
  const hasSimCorpIntegration = enabledModules.has(Module.SIMCORP_INTEGRATION);
  const hasSidecarFeatures = isSidecarMode && (hasAladdinIntegration || hasCRDIntegration || hasSimCorpIntegration);
  
  // Determine the external platform name for UI labels
  const externalPlatformName = hasAladdinIntegration ? 'Aladdin' :
                               hasCRDIntegration ? 'Charles River' :
                               hasSimCorpIntegration ? 'SimCorp' : null;
  
  // Deployment mode for conditional rendering
  const deploymentMode: DeploymentMode = isSidecarMode ? 'sidecar' : 'standalone';

  // Credit module computed properties (Beach Point Capital)
  const hasLeveragedLoans = enabledModules.has(Module.LEVERAGED_LOANS);
  const hasCLOManagement = enabledModules.has(Module.CLO_MANAGEMENT);
  const hasABSTrading = enabledModules.has(Module.ABS_TRADING);
  const hasCDSTrading = enabledModules.has(Module.CDS_TRADING);
  const hasDistressedDebt = enabledModules.has(Module.DISTRESSED_DEBT);
  const hasCovenantMonitoring = enabledModules.has(Module.COVENANT_MONITORING);
  const hasCounterpartyISDA = enabledModules.has(Module.COUNTERPARTY_ISDA);
  const hasRealEstateDebt = enabledModules.has(Module.REAL_ESTATE_DEBT);
  const hasSecondaryIntelligence = enabledModules.has(Module.SECONDARY_INTELLIGENCE);
  const hasCreditAnalytics = enabledModules.has(Module.CREDIT_ANALYTICS);
  const hasAnyCreditModule = hasLeveragedLoans || hasCLOManagement || hasABSTrading || hasCDSTrading ||
    hasDistressedDebt || hasCovenantMonitoring || hasCounterpartyISDA ||
    hasRealEstateDebt || hasSecondaryIntelligence || hasCreditAnalytics;

  const value: FeatureFlagContextState = {
    enabledModules,
    allModules,
    licenseType,
    isSidecarMode,
    deploymentMode,
    isLoading,
    error,
    isModuleEnabled,
    areModulesEnabled,
    anyModuleEnabled,
    refreshLicenses: fetchLicenses,
    hasAIPortfolioConstruction,
    hasAIRebalancer,
    hasAIHedging,
    hasAITaxHarvesting,
    hasAnyAIModule,
    hasIBOR,
    hasCompliance,
    hasRiskAnalytics,
    hasMiddleOffice,
    // Sidecar helpers
    hasSidecarFeatures,
    hasAladdinIntegration,
    hasCRDIntegration,
    hasSimCorpIntegration,
    externalPlatformName,
    // Credit module helpers
    hasLeveragedLoans,
    hasCLOManagement,
    hasABSTrading,
    hasCDSTrading,
    hasDistressedDebt,
    hasCovenantMonitoring,
    hasCounterpartyISDA,
    hasRealEstateDebt,
    hasSecondaryIntelligence,
    hasCreditAnalytics,
    hasAnyCreditModule,
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * Hook to access feature flags
 */
export const useFeatureFlags = (): FeatureFlagContextState => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

/**
 * Hook to check if a specific module is enabled
 */
export const useModuleEnabled = (module: Module): boolean => {
  const { isModuleEnabled } = useFeatureFlags();
  return isModuleEnabled(module);
};

/**
 * Hook to check if multiple modules are all enabled
 */
export const useModulesEnabled = (...modules: Module[]): boolean => {
  const { areModulesEnabled } = useFeatureFlags();
  return areModulesEnabled(...modules);
};

/**
 * Module display info for UI
 */
export const MODULE_DISPLAY_INFO: Record<Module, { name: string; description: string; icon: string }> = {
  [Module.CORE_OMS]: {
    name: 'Core OMS',
    description: 'Basic trade entry, blotter, and positions',
    icon: '📊'
  },
  [Module.IBOR]: {
    name: 'IBOR',
    description: 'Investment Book of Record - NAV, pricing, settlement',
    icon: '📚'
  },
  [Module.COMPLIANCE]: {
    name: 'Compliance',
    description: 'Rules engine and pre-trade compliance checks',
    icon: '✅'
  },
  [Module.RISK_ANALYTICS]: {
    name: 'Risk & Analytics',
    description: 'Risk dashboard and performance attribution',
    icon: '📈'
  },
  [Module.AI_PORTFOLIO_CONSTRUCTION]: {
    name: 'AI Portfolio Construction',
    description: 'Agentic multi-objective portfolio optimization',
    icon: '🤖'
  },
  [Module.AI_REBALANCER]: {
    name: 'AI Rebalancer',
    description: 'Agentic drift detection and rebalancing',
    icon: '⚖️'
  },
  [Module.AI_HEDGING]: {
    name: 'AI Hedging',
    description: 'Agentic duration/convexity hedging recommendations',
    icon: '🛡️'
  },
  [Module.AI_TAX_HARVESTING]: {
    name: 'AI Tax Harvesting',
    description: 'Agentic tax-loss harvesting with wash sale awareness',
    icon: '💰'
  },
  [Module.MIDDLE_OFFICE]: {
    name: 'Middle Office',
    description: 'Reconciliation, corporate actions, cash management',
    icon: '🏢'
  },
  [Module.SWIFT_RECON]: {
    name: 'SWIFT Recon',
    description: 'Custodian reconciliation via SWIFT MT535/540/541',
    icon: '🔄'
  },
  [Module.CORPORATE_ACTIONS]: {
    name: 'Corporate Actions',
    description: 'Mandatory and voluntary corporate action processing',
    icon: '📋'
  },
  [Module.NAV_CALCULATION]: {
    name: 'NAV Reporting',
    description: 'Daily / quarterly NAV calculation and reporting',
    icon: '📊'
  },
  [Module.INSURANCE_REPORTING]: {
    name: 'Insurance Reporting',
    description: 'NAIC Schedule D / RBC / IMR analytics',
    icon: '🏛️'
  },
  [Module.FIX_CONNECTIVITY]: {
    name: 'FIX Connectivity',
    description: 'Trading venue FIX protocol integration',
    icon: '🔌'
  },
  [Module.REGULATORY_REPORTING]: {
    name: 'Regulatory Reporting',
    description: 'Form 13F, ADV, PF reporting',
    icon: '📋'
  },
  [Module.ALADDIN_INTEGRATION]: {
    name: 'Aladdin Integration',
    description: 'BlackRock Aladdin adapter for sidecar mode',
    icon: '🔗'
  },
  [Module.CRD_INTEGRATION]: {
    name: 'CRD Integration',
    description: 'Charles River adapter for sidecar mode',
    icon: '🔗'
  },
  [Module.SIMCORP_INTEGRATION]: {
    name: 'SimCorp Integration',
    description: 'SimCorp Dimension adapter for sidecar mode',
    icon: '🔗'
  },
  // Retail/RIA modules
  [Module.HOUSEHOLDS]: {
    name: 'Households',
    description: 'Group accounts by household with aggregate views',
    icon: '👨‍👩‍👧‍👦'
  },
  [Module.MODEL_PORTFOLIOS]: {
    name: 'Model Portfolios',
    description: 'Model creation, drift monitoring, and rebalancing',
    icon: '📐'
  },
  [Module.FEE_BILLING]: {
    name: 'Fee Billing',
    description: 'AUM-based, tiered, and flat fee billing',
    icon: '💵'
  },
  [Module.CLIENT_PORTAL]: {
    name: 'Client Portal',
    description: 'Client-facing portal for statements and reports',
    icon: '🌐'
  },
  [Module.CUSTODIAN_SCHWAB]: {
    name: 'Schwab Integration',
    description: 'Schwab Advisor Services integration',
    icon: '🏦'
  },
  [Module.CUSTODIAN_FIDELITY]: {
    name: 'Fidelity Integration',
    description: 'Fidelity Institutional integration',
    icon: '🏦'
  },
  [Module.SLEEVE_UMA]: {
    name: 'Sleeve/UMA',
    description: 'Unified Managed Account sleeve management',
    icon: '📁'
  },
  [Module.CLIENT_ONBOARDING]: {
    name: 'Client Onboarding',
    description: 'Digital client onboarding workflows',
    icon: '📝'
  },
  // Credit/Institutional modules (Beach Point Capital)
  [Module.LEVERAGED_LOANS]: {
    name: 'Leveraged Loans',
    description: 'Syndicated loan trading with LSTA T+7 settlement',
    icon: '🏦'
  },
  [Module.CLO_MANAGEMENT]: {
    name: 'CLO Management',
    description: 'CLO deal structuring, waterfall, WARF/WAS/WAL metrics',
    icon: '📊'
  },
  [Module.ABS_TRADING]: {
    name: 'ABS Trading',
    description: 'ABS lifecycle, remittances, triggers, waterfalls, surveillance and Intex analytics',
    icon: '▤'
  },
  [Module.CDS_TRADING]: {
    name: 'CDS Trading',
    description: 'Single-name CDS, CDX/iTraxx indices, tranches',
    icon: '🔄'
  },
  [Module.DISTRESSED_DEBT]: {
    name: 'Distressed Debt',
    description: 'Chapter 11/7 situations, claims trading, DIP financing',
    icon: '⚠️'
  },
  [Module.COVENANT_MONITORING]: {
    name: 'Covenant Monitoring',
    description: 'Financial covenants, step-downs, equity cure tracking',
    icon: '📋'
  },
  [Module.COUNTERPARTY_ISDA]: {
    name: 'Counterparty & ISDA',
    description: 'Counterparty credit limits, ISDA/CSA agreements',
    icon: '🤝'
  },
  [Module.REAL_ESTATE_DEBT]: {
    name: 'Real Estate Debt',
    description: 'CRE loans, LTV/DSCR covenants, construction draws',
    icon: '🏢'
  },
  [Module.SECONDARY_INTELLIGENCE]: {
    name: 'Secondary Intelligence',
    description: 'BWIC/OWIC tracking, dealer color, trade flow analysis',
    icon: '🎯'
  },
  [Module.CREDIT_ANALYTICS]: {
    name: 'Credit Analytics',
    description: 'Spread analytics, recovery analysis, risk metrics',
    icon: '📈'
  },
};

export default FeatureFlagContext;
